/**
 * ProposalSummaryCard Component
 * Displays AI-generated proposal summary with key points and capabilities
 */

import React from 'react';
import { Card, Button } from '@tracekit/core';
import { useProposalSummary, ProposalSummaryResponse } from '../hooks/useProposalSummary';
import { useWorkspace } from '@tracekit/core';
import {
  Sparkles,
  Loader2,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  RefreshCw,
} from 'lucide-react';

interface ProposalSummaryCardProps {
  opportunityId: string | null;
  className?: string;
}

export function ProposalSummaryCard({ opportunityId, className = '' }: ProposalSummaryCardProps) {
  const { projectId } = useWorkspace();
  const [forceRefresh, setForceRefresh] = React.useState(false);
  
  // Use useMemo to stabilize the request object to prevent unnecessary re-fetches
  const request = React.useMemo(() => ({
    include_past_proposals: true,
    include_capabilities: true,
    include_sops: true,
    max_length: 500,
  }), []);
  
  const {
    data: summary,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useProposalSummary(opportunityId, projectId || undefined, request, !!opportunityId && !!projectId, forceRefresh);

  // Reset forceRefresh after the query completes
  React.useEffect(() => {
    if (!isLoading && forceRefresh) {
      setForceRefresh(false);
    }
  }, [isLoading, forceRefresh]);

  const handleRegenerate = async () => {
    setForceRefresh(true);
  };

  if (!opportunityId) {
    return null;
  }

  // Convert markdown to HTML for proper formatting
  const convertMarkdownToHTML = (markdown: string): string => {
    if (!markdown) return '';
    
    let html = markdown;
    
    // Escape HTML first
    html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    
    // Bold / italic / headers - high contrast in dark mode for Overview readability
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>');
    html = html.replace(/__([^_]+)__/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>');
    html = html.replace(/\*([^*]+)\*/g, '<em class="italic text-gray-700 dark:text-slate-200">$1</em>');
    html = html.replace(/_([^_]+)_/g, '<em class="italic text-gray-700 dark:text-slate-200">$1</em>');
    html = html.replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold text-gray-900 dark:text-white mt-4 mb-2">$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold text-gray-900 dark:text-white mt-5 mb-3">$1</h2>');
    html = html.replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold text-gray-900 dark:text-white mt-6 mb-4">$1</h1>');
    
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Line breaks - ensure every paragraph has dark mode text color for Overview visibility
    const pClass = 'mb-3 text-gray-700 dark:text-slate-100';
    html = html.replace(/\n\n/g, `</p><p class="${pClass}">`);
    html = html.replace(/\n/g, '<br />');
    
    if (!html.startsWith('<')) {
      html = `<p class="${pClass}">${html}</p>`;
    }
    
    return html;
  };

  return (
    <Card className={`p-3 bg-gradient-to-br from-blue-50 to-indigo-50 dark:!bg-slate-800 dark:[background-image:none] dark:!border-slate-600 border-blue-200 dark:border-slate-600 ${className}`}>
      {/* Ultra-Condensed Header */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1">
          <Sparkles className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
          <h3 className="font-semibold text-sm text-gray-900 dark:text-black">
            Proposal AI Summary
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {summary && (
            <span className="text-xs px-1.5 py-0.5 bg-white dark:!bg-slate-700 dark:!text-slate-100 rounded border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-200">
              {summary.summary_source === 'rag_analysis' ? 'AI' : 'Basic'}
            </span>
          )}
          {summary && !isLoading && (
            <button
              onClick={handleRegenerate}
              disabled={isRefetching || forceRefresh}
              className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition-colors disabled:opacity-50"
              title="Regenerate summary"
            >
              <RefreshCw className={`h-4 w-4 text-blue-600 dark:text-blue-400 ${isRefetching || forceRefresh ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin" />
            <p className="text-sm text-gray-600 dark:text-slate-200">
              Generating AI summary...
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="py-6">
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                Failed to generate summary
              </p>
              <p className="text-xs text-red-600 dark:text-red-400 mb-3">
                {error instanceof Error ? error.message : 'An unexpected error occurred'}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isRefetching}
                className="text-xs"
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${isRefetching ? 'animate-spin' : ''}`} />
                Retry
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Content - inner wrapper forces dark bg in dark mode so all text (headings + body) is readable */}
      {summary && !isLoading && (
        <div className="space-y-3 dark:bg-slate-800 dark:rounded-lg dark:-mx-1 dark:px-1 dark:py-1">
          {/* Main Summary with Markdown Rendering - headings and body must contrast with both light and dark card */}
          <div 
            className="text-sm text-gray-700 dark:!text-slate-100 leading-relaxed prose prose-sm max-w-none [&_h1]:!text-gray-900 [&_h1]:dark:!text-white [&_h2]:!text-gray-900 [&_h2]:dark:!text-white [&_h3]:!text-gray-900 [&_h3]:dark:!text-white [&_p]:!text-gray-700 [&_p]:dark:!text-slate-100 [&_strong]:!text-gray-900 [&_strong]:dark:!text-white [&_em]:!text-gray-600 [&_em]:dark:!text-slate-200"
            dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(summary.summary) }}
          />

          {/* Key Points */}
          {summary.key_points && summary.key_points.length > 0 && (
            <div className="pt-2 border-t border-blue-200 dark:border-slate-600">
              <h4 className="text-xs font-semibold text-gray-900 dark:!text-slate-100 mb-1.5 flex items-center gap-1.5">
                <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                <span className="dark:text-slate-100">Key Points</span>
              </h4>
              <ul className="space-y-1">
                {summary.key_points.map((point, index) => (
                  <li key={index} className="text-xs text-gray-700 dark:!text-slate-100 flex items-start gap-1.5">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span className="dark:text-slate-100">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Relevant Capabilities */}
          {summary.relevant_capabilities && summary.relevant_capabilities.length > 0 && (
            <div className="pt-2 border-t border-blue-200 dark:border-slate-600">
              <h4 className="text-xs font-semibold text-gray-900 dark:!text-slate-100 mb-1.5 flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                <span className="dark:text-slate-100">Relevant Capabilities</span>
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {summary.relevant_capabilities.map((capability, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:!bg-purple-900/50 dark:text-black border border-purple-200 dark:border-purple-700"
                  >
                    {capability}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

